import { useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ShoppingBagIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import i18next from 'i18next';
import { EyeIcon } from '@heroicons/react/24/solid';
import useConfirmation from '../../utils/useConfirmation';
import Notify from '../../utils/useNotify';
import { useRefresh } from '../../utils/RefreshProvider';
import { truncateString } from '../../utils/truncateString';
import { useNavigate } from 'react-router';
import { fileNotFound } from '../../assets/images/icons';

export default function ShoppingCart() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const session_id = localStorage.getItem('session_id');
  const localhost = process.env.REACT_APP_LOCALHOST;
  const [open, setOpen] = useState(false);
  const { refreshKey } = useRefresh();
  const [count, setCount] = useState(0);
  const [myOrders, setMyOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        if (token !== null || session_id !== null) {
          const res = await axios.post(localhost + "count-orders", { session_id }, { headers: { Authorization: `Bearer ${token}` } });
          setCount(res.data.length);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchCount();
  }, [refreshKey]);


  const handleShoppingCart = async () => {
    setOpen(true);
    try {
      if (token !== null || session_id !== null) {
        const res = await axios.post(localhost + "shopping-cart", { session_id }, { headers: { Authorization: `Bearer ${token}` } });
        setMyOrders(res.data);
      }
    } catch (err) {
      // 
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalAmount = () => {
    return myOrders.reduce((sum, order) => sum + parseFloat(order.total_amount), 0).toFixed(2);
  };

  return (
    <>
      <button onClick={handleShoppingCart} className="group relative inline-flex items-center gap-2 rounded-full px-2 md:px-4 py-2 transition-colors duration-200 hover:bg-gray-100 active:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-2">
        <ShoppingBagIcon className="h-6 w-6 text-black transition-colors duration-200 group-hover:text-black/80 group-active:text-black" />
        {count > 0 && (
          <span className="max-md:hidden flex items-center justify-center rounded-full bg-black hover:bg-black/80 px-2.5 py-0.5 text-sm font-medium text-white transition-transform duration-200 group-hover:scale-105">
            {count}
          </span>
        )}
      </button>

      <Dialog open={open} onClose={setOpen} className="relative z-40">
        <DialogBackdrop transition className="fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <DialogPanel transition className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700">
                <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                  <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
                    <div className="flex items-start justify-between">
                      <DialogTitle className="text-lg font-medium text-black">{t("shopping-cart")}</DialogTitle>
                      <div className="ml-3 flex h-7 items-center">
                        <button type="button" onClick={() => setOpen(false)} className="relative -m-2 p-2 text-gray-400 hover:text-gray-500" >
                          <span className="absolute -inset-0.5" />
                          <XMarkIcon aria-hidden="true" className="size-6" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-8">
                      <div className="flow-root">
                        {
                          loading ? (
                            <div className="min-h-[55vh] flex items-center justify-center">
                              <div className="loading-spinner" />
                            </div>
                          ) : (
                            myOrders.length > 0 ? (
                              <ul role="list" className="-my-6 divide-y divide-gray-200">
                                {myOrders.map((product, index) => <OrderCard key={index} order={product} />)}
                              </ul>
                            ) : (
                              <div className="flex flex-col text-center justify-center text-sm min-h-[60vh]">
                                <p className='font-semibold text-black mb-2'>{t("empty-bag")}</p>
                              </div>
                            ))
                        }
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 px-4 py-6 sm:px-6">
                    <div className="flex justify-between text-base font-medium text-black">
                      <p>{t("subtotal")}</p>
                      <div className="ml-2 flex items-end">
                        <p className="text-xl leading-none">{calculateTotalAmount()}</p>
                        <span className='ml-0.5 text-xs leading-none pb-[1px]'>MAD</span>
                      </div>
                    </div>
                    <p className="mt-0.5 text-sm text-gray-500">{t("shipping-text")}</p>
                    <div className="mt-6">
                      <button type='button' disabled={myOrders.length <= 0} onClick={() => navigate("/checkout")}
                        className="disabled:bg-black/40 w-full flex items-center justify-center rounded-md border border-transparent bg-black px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-black/80">
                        {t("checkout")}
                      </button>
                    </div>
                    <div className="mt-6 flex justify-center text-center text-sm text-gray-500">
                      <p>
                        {t("or")}{' '}
                        <a href='/shop' className="font-medium text-orange-600 hover:text-orange-500">
                          {t("continue-shopping")}
                          <span aria-hidden="true"> &rarr;</span>
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  )
}

const OrderCard = ({ order }) => {
  const { t } = useTranslation();
  const localimg = process.env.REACT_APP_LOCALHOST_IMG;
  const image = JSON.parse(order.imgs);
  const category = JSON.parse(order.category_name);
  const productName = JSON.parse(order.product_name);
  const color = order.color_hex ? order.color_hex : null;

  return (
    <li className="flex py-6">
      <a href={"/product/" + order.slug} className="group relative size-24 shrink-0 overflow-hidden rounded-md border border-gray-200">
        <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <EyeIcon className="absolute inset-0 m-auto size-5 text-white hidden group-hover:block z-10" />
        <img loading="lazy" alt={order.slug} src={image? localimg + image[0]:fileNotFound} className="size-full object-cover transition-all duration-300 group-hover:blur-sm" />
      </a>

      <div className="ml-4 flex flex-1 flex-col">
        <div className="flex justify-between items-start text-base font-medium text-black mb-1">
          <div>
            <a href={order.slug} className='text-base font-semibold leading-3 hover:underline'>{truncateString(productName[i18next.language], 30)}</a>
            <p className="text-sm text-gray-700 leading-none">{truncateString(category[i18next.language], 30)}</p>
          </div>
          <RemoveOrder productId={order.product_id} sizeId={order.size_id} colorId={order.color_id} />
        </div>
        <div className="flex flex-1 items-end justify-between">
          <div className='text-[13px] text-gray-700 space-y-0.5 pb-0.5'>
            {color ? <p className='flex flex-wrap items-center leading-none'>{t("color")} <span className='ml-1 size-4 rounded-full' style={{ background: color }} /></p> : null}
            <p className='leading-none'>{t("size")} {order.size_name}</p>
            <p className='leading-none'>{t("quantity")} {order.total_quantity}</p>
          </div>
          <div className="ml-2 flex items-end">
            <p className="text-xl leading-none">{order.total_amount}</p>
            <span className='ml-0.5 text-xs leading-none pb-[1px]'>MAD</span>
          </div>
        </div>
      </div>
    </li>
  )
}

export const RemoveOrder = ({ productId, sizeId, colorId, btn }) => {
  const { t } = useTranslation();
  const token = localStorage.getItem('token');
  const session_id = localStorage.getItem('session_id');
  const localhost = process.env.REACT_APP_LOCALHOST;
  const { open, ConfirmComponent } = useConfirmation();

  const handleDelete = async () => {
    const data = {
      color_id: colorId,
      size_id: sizeId,
      product_id: productId,
      session_id
    }

    await axios.delete(localhost + "remove-order", { data }, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        Notify('success', t("delete-order-success"));
        setTimeout(() => { window.location.reload() }, 1000);
      }).catch((err) => {
        console.error(err);
        Notify('error', t("delete-order-error"))
      });
  };

  const handleAction = () => {
    open(
      {
        title: t("delete-confirmation-header"),
        text: t("delete-confirmation-body"),
        action: "add",
      },
      handleDelete
    );
  };

  return (
    <>
      {ConfirmComponent}
      <button type='button' onClick={handleAction} className="p-0.5 rounded-md hover:bg-gray-100 text-gray-700 tsem hover:text-black">
        <XMarkIcon className={btn ? 'size-4' : 'size-5'} />
      </button>
    </>
  )
}